/** The 10 training skills and their rank perks (docs/game-design.md §5). */

export type SkillId =
  | 'power'
  | 'speed'
  | 'reach'
  | 'takedown'
  | 'escape'
  | 'guard'
  | 'chin'
  | 'footwork'
  | 'cardio'
  | 'focus';

export type SkillGroup = 'striking' | 'grappling' | 'defense' | 'engine';

export type SkillRanks = Record<SkillId, number>;

export interface SkillDef {
  id: SkillId;
  group: SkillGroup;
  /** Effect per rank as a fraction (0.015 = +1.5%). */
  perRank: number;
  /**
   * How Training shows the effect: +3% or −3% (`percent`, `negativePercent`), or
   * the fight number itself at that rank (D48): the jab's damage (`jab`), the takedown slam's
   * damage (`slam`), a slam's damage on this fighter (`slamTaken`, D49), max health (`health`).
   */
  display: 'percent' | 'negativePercent' | 'jab' | 'slam' | 'slamTaken' | 'health';
  perks: { 5: PerkId; 10: PerkId };
}

export const MAX_RANK = 10;

/** Cost of buying rank r is RANK_COST[r] (index 0 unused). One skill 0→10 costs 22. */
export const RANK_COST = [0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4] as const;

/**
 * D48 (2026-10-03, owner: "when I level up there should be an actual difference"): Power and
 * Takedown 3% a rank (was 1.5%). Speed 5% a rank off the strike's wind-up and its stamina cost
 * (was 1.5% off the whole strike, which bot soaks showed did nothing). Each lands rank 10 at
 * about 80% wins against an untrained twin in bot soaks, like Chin and Cardio.
 * D49 (2026-10-04): Escape 4% a rank off takedown damage taken and time down (was 3% off time
 * down only, which did nothing: nobody can be hit while down). Footwork 3% a rank of movement
 * speed (was 1%), plus FOOTWORK_EVADE.
 * D51 (2026-10-04): Guard +10% a rank on the hit after a block (a READ! punish), plus
 * GUARD_BLOCK (blocking more did nothing: a blocked strike already loses 80%). Reach 2.5% a rank
 * (was 1%). Focus 10% a rank (was 2.5%).
 */
export const SKILLS: readonly SkillDef[] = [
  { id: 'power', group: 'striking', perRank: 0.03, display: 'jab', perks: { 5: 'heavyHands', 10: 'guardCrusher' } }, // placeholder
  { id: 'speed', group: 'striking', perRank: 0.05, display: 'percent', perks: { 5: 'feint', 10: 'oneTwo' } }, // placeholder
  { id: 'reach', group: 'striking', perRank: 0.025, display: 'percent', perks: { 5: 'jabAndMove', 10: 'stopHit' } }, // placeholder
  { id: 'takedown', group: 'grappling', perRank: 0.03, display: 'slam', perks: { 5: 'longShot', 10: 'fenceSlam' } }, // placeholder
  { id: 'escape', group: 'grappling', perRank: 0.04, display: 'slamTaken', perks: { 5: 'standUp', 10: 'reversal' } }, // placeholder
  { id: 'guard', group: 'defense', perRank: 0.1, display: 'percent', perks: { 5: 'parry', 10: 'ironGuard' } }, // placeholder
  { id: 'chin', group: 'defense', perRank: 0.015, display: 'health', perks: { 5: 'tough', 10: 'ironChin' } },
  { id: 'footwork', group: 'defense', perRank: 0.03, display: 'percent', perks: { 5: 'lightFeet', 10: 'cageEscape' } }, // placeholder
  { id: 'cardio', group: 'engine', perRank: 0.02, display: 'percent', perks: { 5: 'secondBreath', 10: 'workhorse' } },
  { id: 'focus', group: 'engine', perRank: 0.1, display: 'percent', perks: { 5: 'headStart', 10: 'quickRelease' } }, // placeholder
];

/**
 * Footwork's second effect (D49): opponents' strikes reach this fighter 1% less a rank (Reach's
 * mirror), so a punch at the edge of range falls short ("Dodged").
 */
export const FOOTWORK_EVADE = 0.01; // placeholder

/** Guard's second effect: strike damage blocked +0.8 percentage points a rank (80% → 88%). */
export const GUARD_BLOCK = 0.008;

export const SKILL_IDS: readonly SkillId[] = SKILLS.map((s) => s.id);
export const SKILL_GROUPS: readonly SkillGroup[] = ['striking', 'grappling', 'defense', 'engine'];

export type PerkId =
  | 'heavyHands'
  | 'guardCrusher'
  | 'feint'
  | 'oneTwo'
  | 'jabAndMove'
  | 'stopHit'
  | 'longShot'
  | 'fenceSlam'
  | 'standUp'
  | 'reversal'
  | 'parry'
  | 'ironGuard'
  | 'tough'
  | 'ironChin'
  | 'lightFeet'
  | 'cageEscape'
  | 'secondBreath'
  | 'workhorse'
  | 'headStart'
  | 'quickRelease';

/** Perk numbers (all placeholders until bot-vs-bot soak tests). Health and damage in tenths. */
export const PERKS = {
  heavyHands: { strikeKnockbackMul: 1.2 },
  guardCrusher: { blockedStrikeStaminaCost: 15, guardBreakStun: 0.3 },
  feint: { window: 0.08, staminaRefund: 4 },
  oneTwo: { window: 0.25, cost: 5, duration: 0.2, hitStart: 0.06, hitEnd: 0.12, damage: 30, knockback: 200, stun: 0.15 },
  jabAndMove: { attackSpeedMul: 0.6 },
  stopHit: {},
  longShot: { contactRadii: 2.3 },
  fenceSlam: { fenceDistanceRadii: 1, damageMul: 1.5 },
  standUp: { grappleImmunity: 0.4 },
  reversal: { window: 0.25, pushKnockback: 300 },
  parry: { window: 0.1, bounceMul: 2 },
  ironGuard: { signatureReduction: 0.75 },
  tough: { strikeStunMul: 0.75 },
  ironChin: { leaveHealth: 10 },
  lightFeet: { blockSpeedMul: 0.5 },
  cageEscape: { fenceDistanceRadii: 1, speedMul: 1.25 },
  secondBreath: { healthBetweenRounds: 150 },
  workhorse: { costMul: 0.8 },
  headStart: { meter: 25 },
  quickRelease: { windup: 0.26 },
} as const satisfies Record<PerkId, object>;

export function emptyRanks(): SkillRanks {
  return {
    power: 0,
    speed: 0,
    reach: 0,
    takedown: 0,
    escape: 0,
    guard: 0,
    chin: 0,
    footwork: 0,
    cardio: 0,
    focus: 0,
  };
}

export function skillDef(id: SkillId): SkillDef {
  const def = SKILLS.find((s) => s.id === id);
  if (!def) throw new Error(`Unknown skill ${id}`);
  return def;
}

/** Perks unlocked by a set of ranks. */
export function unlockedPerks(ranks: SkillRanks): PerkId[] {
  const out: PerkId[] = [];
  for (const s of SKILLS) {
    if (ranks[s.id] >= 5) out.push(s.perks[5]);
    if (ranks[s.id] >= 10) out.push(s.perks[10]);
  }
  return out;
}
